import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DateSelector from './components/DateSelector';
import DestinationSelector from './components/DestinationSelector';
import FerryScheduleCard from './components/FerryScheduleCard';
import LoadingSpinner from './components/LoadingSpinner';
import EmptyState from './components/EmptyState';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { getFerrySchedulesCached } from '../../utils/ferryApi';

const FerryScheduleSearch = () => {
  const navigate = useNavigate();
  // Nouvel état (pas de filtres ni onglets compagnie)
  const [departureDate, setDepartureDate] = useState('');
  const [selectedPort, setSelectedPort] = useState('');
  const [selectedCity, setSelectedCity] = useState(null); // objet { code, nom }
  const [direction, setDirection] = useState('city-to-port'); // 'city-to-port' ou 'port-to-city'
  const [searchResults, setSearchResults] = useState([]);
  const [computedRoute, setComputedRoute] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [apiErrors, setApiErrors] = useState([]);

  const mockDriverCounts = {
    1: 3,
    2: 1,
    3: 5,
    4: 0,
    5: 2,
    6: 4,
    7: 1,
    8: 3
  };

  // Initialize with today's date
  useEffect(() => {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    setDepartureDate(today);
  }, []);

  // Simulate API call when search parameters change
  // Recalculer la route normalisée en fonction du sens (on ne garde pour l'instant que la partie port car les mocks reposent sur le port)
  useEffect(() => {
    if (!selectedPort) { setComputedRoute(''); return; }
    // normalisation: vers-port-xxx signifie qu'on va vers le port (depuis ville) => direction city-to-port
    // l'inverse port-to-city utilisera depuis-port-xxx (pour distinguer si besoin plus tard)
    const token = selectedPort;
    const route = direction === 'city-to-port' ? `vers-port-${token}` : `depuis-port-${token}`;
    setComputedRoute(route);
  }, [selectedPort, direction]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (departureDate && computedRoute && selectedCity) {
        setIsLoading(true);
        setApiErrors([]);
        try {
          const normalized = computedRoute?.replace('depuis-', 'vers-');
          const port = normalized.split('vers-port-')?.[1];
          const { schedules, errors } = await getFerrySchedulesCached({ date: departureDate, port });
          if (cancelled) return;
            const filtered = schedules.filter(s => s.route === normalized);
            setSearchResults(filtered);
            setApiErrors(errors || []);
        } catch (e) {
          if (!cancelled) setApiErrors([{ company: 'all', message: e.message }]);
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      } else {
        setSearchResults([]);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [departureDate, computedRoute, selectedCity]);

  const handleForceRefresh = async () => {
    if (!(departureDate && computedRoute && selectedCity)) return;
    setIsLoading(true);
    setApiErrors([]);
    try {
      const normalized = computedRoute?.replace('depuis-', 'vers-');
      const port = normalized.split('vers-port-')?.[1];
      const { schedules, errors } = await getFerrySchedulesCached({ date: departureDate, port }, { force: true });
      const filtered = schedules.filter(s => s.route === normalized);
      setSearchResults(filtered);
      setApiErrors(errors || []);
    } catch (e) {
      setApiErrors([{ company: 'all', message: e.message }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectFerry = (schedule) => {
    navigate('/booking-confirmation', { 
      state: { 
        ferry: schedule, 
        date: departureDate,
        route: computedRoute
      } 
    });
  };

  const handleCreateTrip = () => navigate('/create-trip');

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Search" size={24} color="var(--color-primary)" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Recherche de trajets</h1>
          </div>
          <p className="text-muted-foreground">
            Trouvez des covoiturages basés sur les horaires officiels des ferries
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne critères */}
          <div className="space-y-6 lg:col-span-1">
            <DestinationSelector
              selectedPort={selectedPort}
              onPortChange={setSelectedPort}
              selectedCity={selectedCity}
              onCityChange={setSelectedCity}
              direction={direction}
              onDirectionChange={setDirection}
            />
            <DateSelector
              departureDate={departureDate}
              onDepartureDateChange={setDepartureDate}
            />
          </div>
          {/* Résultats */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-lg p-6 maritime-shadow-sm border border-border">
              {/* Results Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    Horaires disponibles
                  </h2>
                   {departureDate && computedRoute && selectedCity && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {formatDate(departureDate)} • {direction === 'city-to-port' ? `${selectedCity?.nom} → ${selectedPort || 'Port'}` : `${selectedPort || 'Port'} → ${selectedCity?.nom}`}
                    </p>
                  )}
                </div>
                
                {searchResults?.length > 0 && (
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Icon name="Clock" size={16} />
                      <span>Données en cache (max 2 min)</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="RefreshCw"
                      onClick={handleForceRefresh}
                      disabled={isLoading}
                    >
                      Recharger
                    </Button>
                  </div>
                )}
              </div>

              {/* API Errors */}
              {apiErrors?.length > 0 && (
                <div className="mb-4 p-3 bg-warning/10 border border-warning/30 rounded-md text-sm space-y-1">
                  {apiErrors.map((err, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Icon name="AlertTriangle" size={14} className="text-warning" />
                      <span>
                        {err.company === 'yeu-continent' && 'Yeu Continent'}
                        {err.company === 'compagnie-vendeenne' && 'Compagnie Vendéenne'}
                        {err.company === 'all' && 'APIs ferries'} : {err.message}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Loading State */}
              {isLoading && (
                <LoadingSpinner message="Synchronisation avec les API maritimes..." />
              )}

              {/* Empty State */}
              {!isLoading && (!departureDate || !selectedPort || !selectedCity) && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Calendar" size={32} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Saisissez port + ville puis date
                  </h3>
                  <p className="text-muted-foreground">
                    Indiquez la ville et le port, vous pouvez inverser le sens, puis choisissez une date
                  </p>
                </div>
              )}

              {/* No Results */}
              {!isLoading && departureDate && computedRoute && selectedCity && searchResults?.length === 0 && (
                <EmptyState onCreateTrip={handleCreateTrip} />
              )}

              {/* Results Grid */}
              {!isLoading && searchResults?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {searchResults?.map((schedule) => (
                    <FerryScheduleCard
                      key={schedule?.id}
                      schedule={schedule}
                      availableDrivers={mockDriverCounts?.[schedule?.id] || 0}
                      onSelectFerry={handleSelectFerry}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            {!isLoading && searchResults?.length > 0 && (
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={handleCreateTrip}
                  iconName="Plus"
                  iconPosition="left"
                  className="flex-1"
                >
                  Créer mon trajet
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleForceRefresh}
                  iconName="RefreshCw"
                  iconPosition="left"
                  className="flex-1"
                  disabled={isLoading}
                >
                  Rafraîchir (forcer API)
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default FerryScheduleSearch;